import { useState } from "react";
import { ArrowUpDown, Loader2, User, Trash2 } from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog";

const BUCKET_STYLES = {
  "Exceptional Hire": "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/70 dark:text-emerald-200 dark:border-emerald-800",
  "Strong Hire": "bg-sky-100 text-blue-900 border-sky-300 dark:bg-blue-950/70 dark:text-sky-200 dark:border-blue-900",
  Hire: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950/70 dark:text-sky-200 dark:border-sky-800",
  Borderline: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/70 dark:text-amber-200 dark:border-amber-800",
  "Do Not Proceed": "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/70 dark:text-rose-200 dark:border-rose-800",
};

export default function CandidateRankingTable({ candidates, onSelect, pending = [], onDelete }) {
  const [sortField, setSortField] = useState("overall_score");
  const [asc, setAsc] = useState(false);

  const sorted = [...candidates].sort((a, b) => {
    if (sortField === "candidate_name") {
      return asc ? a.candidate_name.localeCompare(b.candidate_name) : b.candidate_name.localeCompare(a.candidate_name);
    }
    return asc ? a.overall_score - b.overall_score : b.overall_score - a.overall_score;
  });

  const toggleSort = (field) => {
    if (field === sortField) setAsc(!asc);
    else {
      setSortField(field);
      setAsc(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border/60">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="candidate-ranking-table">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("candidate_name")}>
                Candidate <ArrowUpDown className="inline h-3 w-3" />
              </th>
              <th className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("overall_score")}>
                Score <ArrowUpDown className="inline h-3 w-3" />
              </th>
              <th className="px-4 py-3">Recommendation</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((p, idx) => (
              <tr
                key={`pending-${idx}`}
                className="animate-in fade-in-0 border-b border-border/60 bg-primary/[0.03] last:border-0"
                data-testid={`pending-row-${idx}`}
              >
                <td className="px-4 py-3 text-muted-foreground">—</td>
                <td className="px-4 py-3 font-medium text-muted-foreground">{p.fileName}</td>
                <td className="px-4 py-3" colSpan={3}>
                  <span className="flex items-center gap-2 text-xs font-medium text-primary">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {p.status === "processing" ? "Evaluating with AI..." : "Queued"}
                  </span>
                </td>
              </tr>
            ))}
            {sorted.map((c, idx) => (
              <tr
                key={c.id}
                className="animate-in fade-in-0 border-b border-border/60 last:border-0 transition-colors duration-150 hover:bg-muted/40"
                data-testid={`candidate-row-${c.id}`}
              >
                <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <User className="h-3.5 w-3.5" />
                    </div>
                    <span className="font-medium">{c.candidate_name}</span>
                    {c.error && <span className="text-xs text-destructive">(Error)</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono font-semibold" data-testid={`candidate-score-badge-${c.id}`}>
                    {c.overall_score}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${BUCKET_STYLES[c.recommendation] || ""}`}>
                    {c.recommendation}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onSelect(c)}
                      data-testid={`view-report-btn-${c.id}`}
                      className="text-primary hover:underline text-sm font-medium"
                    >
                      View Report
                    </button>
                    {onDelete && (
                      <ConfirmDialog
                        testId={`delete-candidate-dialog-${c.id}`}
                        title={`Remove ${c.candidate_name}?`}
                        description="This deletes this candidate's evaluation and report from the batch. The credits already spent are not refunded and this cannot be undone."
                        confirmLabel="Remove candidate"
                        onConfirm={() => onDelete(c)}
                      >
                        <button
                          title="Remove candidate"
                          aria-label={`Remove ${c.candidate_name}`}
                          data-testid={`delete-candidate-btn-${c.id}`}
                          className="rounded-md p-1 text-muted-foreground transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </ConfirmDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
